import React from "react";

/**
 * Rank insignia. 20 unique military-style badges that scale from simple
 * (early ranks) to edgy / ornate (late ranks). Each badge shares a clean
 * shield silhouette but layers different glyphs (chevrons, crossed bars,
 * swords, wings, crown, skull) by tier.
 *
 * Props:
 *   - rankId (1–20)
 *   - size: "sm" (28px), "md" (60px), "lg" (140px). Defaults to "md".
 *   - locked: bool — when true renders a muted greyscale silhouette only.
 *   - className: extra wrapping classes.
 */
const TIER_COLORS = {
    // tier ranges: 1-4 Foundation, 5-8 Progression, 9-12 Ascension,
    // 13-16 Elite, 17-19 Apex, 20 Legend
    Foundation:  { base: "#a8a8a8", edge: "#5a5a5a", glyph: "#1a1a1a" }, // grey steel
    Progression: { base: "#cd7f32", edge: "#7a4818", glyph: "#1a1a1a" }, // bronze
    Ascension:   { base: "#d4af37", edge: "#7a6217", glyph: "#1a1a1a" }, // gold
    Elite:       { base: "#c0392b", edge: "#5a1810", glyph: "#0a0a0a" }, // crimson
    Apex:        { base: "#0a0a0a", edge: "#ff3b30", glyph: "#ff3b30" }, // obsidian + Electric Blaze
    Legend:      { base: "#ff3b30", edge: "#7a0a08", glyph: "#fff8b0" }, // Electric Blaze + ember gold
};

const RANK_TIERS = {
     1: "Foundation",  2: "Foundation",  3: "Foundation",  4: "Foundation",
     5: "Progression", 6: "Progression", 7: "Progression", 8: "Progression",
     9: "Ascension",  10: "Ascension",  11: "Ascension",  12: "Ascension",
    13: "Elite",      14: "Elite",      15: "Elite",      16: "Elite",
    17: "Apex",       18: "Apex",       19: "Apex",
    20: "Legend",
};

const SIZE_PX = { sm: 28, md: 60, lg: 140 };

export default function RankBadge({ rankId, size = "md", locked = false, className = "" }) {
    const px = SIZE_PX[size] || SIZE_PX.md;
    const safeId = Math.max(1, Math.min(20, rankId || 1));
    const tier = RANK_TIERS[safeId];
    const c = locked ? { base: "#2b2b2b", edge: "#444", glyph: "#666" } : TIER_COLORS[tier];

    // Glyph composition by rank — adds layers as the user climbs.
    const tierIndex = safeId - 1; // 0..19
    const tierPos = (safeId - 1) % 4; // 0..3 within a tier (Foundation/Progression/etc)

    return (
        <div className={`relative inline-block ${className}`} style={{ width: px, height: px }} data-testid={`rank-badge-${safeId}`}>
            <svg viewBox="0 0 100 100" width={px} height={px} className="drop-shadow-md">
                <defs>
                    <linearGradient id={`grad-${safeId}-${size}`} x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor={c.base} stopOpacity={locked ? 0.4 : 1} />
                        <stop offset="100%" stopColor={c.edge} stopOpacity={locked ? 0.4 : 1} />
                    </linearGradient>
                </defs>

                {/* Shield base — silhouette shared across all ranks */}
                <path
                    d="M 50 6 L 90 22 L 90 54 Q 90 78 50 96 Q 10 78 10 54 L 10 22 Z"
                    fill={`url(#grad-${safeId}-${size})`}
                    stroke={c.edge}
                    strokeWidth="2"
                />

                {/* Inner bezel */}
                <path
                    d="M 50 14 L 84 26 L 84 54 Q 84 74 50 88 Q 16 74 16 54 L 16 26 Z"
                    fill="none"
                    stroke={c.glyph}
                    strokeWidth="1"
                    opacity="0.5"
                />

                {/* === GLYPHS by tier — composition grows with rank === */}
                {/* Foundation 1-4: chevrons (1, 2, 3, 3+star) */}
                {tier === "Foundation" && (
                    <g fill={c.glyph}>
                        {Array.from({ length: Math.min(3, safeId) }).map((_, i) => (
                            <polygon key={i} points={`50,${42 - i * 9} 38,${52 - i * 9} 50,${48 - i * 9} 62,${52 - i * 9}`} />
                        ))}
                        {safeId === 4 && <polygon points="50,68 53,76 61,76 55,80 57,88 50,84 43,88 45,80 39,76 47,76" />}
                    </g>
                )}

                {/* Progression 5-8: crossed-bar with rising number of pips */}
                {tier === "Progression" && (
                    <g fill={c.glyph} stroke={c.glyph} strokeWidth="2">
                        <rect x="30" y="46" width="40" height="8" rx="1" />
                        {Array.from({ length: tierPos + 1 }).map((_, i) => (
                            <circle key={i} cx={30 + i * 14} cy={64} r={3.5} fill={c.glyph} />
                        ))}
                    </g>
                )}

                {/* Ascension 9-12: crossed swords + chevron wings */}
                {tier === "Ascension" && (
                    <g stroke={c.glyph} strokeWidth="2.5" strokeLinecap="round" fill="none">
                        <line x1="28" y1="32" x2="72" y2="76" />
                        <line x1="72" y1="32" x2="28" y2="76" />
                        {/* hilts */}
                        <line x1="22" y1="26" x2="34" y2="38" />
                        <line x1="78" y1="26" x2="66" y2="38" />
                        {/* tier pip column */}
                        {Array.from({ length: tierPos + 1 }).map((_, i) => (
                            <circle key={i} cx={50} cy={80 - i * 6} r={2.5} fill={c.glyph} stroke="none" />
                        ))}
                    </g>
                )}

                {/* Elite 13-16: laurel wreath flanking a central diamond */}
                {tier === "Elite" && (
                    <g fill={c.glyph} stroke={c.glyph} strokeWidth="1.4">
                        <path d="M 30 60 Q 24 50 30 36 Q 36 30 40 32" fill="none" />
                        <path d="M 70 60 Q 76 50 70 36 Q 64 30 60 32" fill="none" />
                        {/* leaves */}
                        {[40, 50, 60].map(y => <ellipse key={`l${y}`} cx={28} cy={y} rx={4} ry={2.5} transform={`rotate(-30 28 ${y})`} />)}
                        {[40, 50, 60].map(y => <ellipse key={`r${y}`} cx={72} cy={y} rx={4} ry={2.5} transform={`rotate(30 72 ${y})`} />)}
                        {/* center diamond */}
                        <polygon points="50,38 62,52 50,66 38,52" fill={tierPos >= 1 ? "#ff3b30" : c.glyph} />
                        {Array.from({ length: tierPos }).map((_, i) => (
                            <circle key={i} cx={50 + (i - 1) * 6} cy={76} r={2} />
                        ))}
                    </g>
                )}

                {/* Apex 17-19: skull silhouette with wings + spikes */}
                {tier === "Apex" && (
                    <g>
                        {/* wings */}
                        <path d="M 12 50 Q 24 40 36 50 Q 28 46 22 56 Q 18 50 12 50" fill="#ff3b30" opacity="0.85" />
                        <path d="M 88 50 Q 76 40 64 50 Q 72 46 78 56 Q 82 50 88 50" fill="#ff3b30" opacity="0.85" />
                        {/* skull head */}
                        <ellipse cx="50" cy="48" rx="14" ry="13" fill="#0a0a0a" stroke="#ff3b30" strokeWidth="1.5" />
                        <ellipse cx="44.5" cy="48" rx="2.5" ry="3.5" fill="#ff3b30" />
                        <ellipse cx="55.5" cy="48" rx="2.5" ry="3.5" fill="#ff3b30" />
                        {/* jaw teeth */}
                        <rect x="42" y="58" width="16" height="6" fill="#0a0a0a" stroke="#ff3b30" strokeWidth="1" />
                        {[44, 47.5, 51, 54.5].map(x => <line key={x} x1={x} y1={58} x2={x} y2={64} stroke="#ff3b30" strokeWidth="0.8" />)}
                        {/* spikes — number grows with rank */}
                        {Array.from({ length: tierPos + 1 }).map((_, i) => (
                            <polygon
                                key={i}
                                points={`${30 + i * 20},78 ${33 + i * 20},86 ${27 + i * 20},86`}
                                fill="#ff3b30"
                            />
                        ))}
                    </g>
                )}

                {/* Legend 20: crown + flame + skull combo */}
                {tier === "Legend" && (
                    <g>
                        {/* crown spikes above */}
                        <polygon points="30,18 36,8 42,18 48,6 54,18 60,8 66,18 70,18 70,24 30,24" fill={c.glyph} stroke="#7a0a08" strokeWidth="1" />
                        {/* center flame skull */}
                        <ellipse cx="50" cy="56" rx="16" ry="15" fill="#0a0a0a" stroke={c.glyph} strokeWidth="1.5" />
                        <ellipse cx="43.5" cy="56" rx="3" ry="4" fill={c.glyph} />
                        <ellipse cx="56.5" cy="56" rx="3" ry="4" fill={c.glyph} />
                        <rect x="41" y="68" width="18" height="7" fill="#0a0a0a" stroke={c.glyph} strokeWidth="1" />
                        {[44, 48, 52, 56].map(x => <line key={x} x1={x} y1={68} x2={x} y2={75} stroke={c.glyph} strokeWidth="0.8" />)}
                        {/* flames */}
                        <path d="M 32 30 Q 28 22 34 14 Q 36 22 40 18 Q 42 24 38 30" fill="#ff3b30" opacity="0.85" />
                        <path d="M 68 30 Q 72 22 66 14 Q 64 22 60 18 Q 58 24 62 30" fill="#ff3b30" opacity="0.85" />
                        {/* base banner */}
                        <rect x="22" y="86" width="56" height="6" fill={c.glyph} />
                    </g>
                )}

                {locked && (
                    <g>
                        <rect x="40" y="44" width="20" height="20" rx="2" fill="#0a0a0a" stroke="#666" strokeWidth="1.5" />
                        <path d="M 44 44 V 38 Q 44 32 50 32 Q 56 32 56 38 V 44" fill="none" stroke="#666" strokeWidth="2" />
                    </g>
                )}
            </svg>
        </div>
    );
}
